import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { Loader2, Copy, RefreshCw, Code } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ORESTORE_LOGO = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/3azwctos_Ore-Store-LOGO2.png";
const WOODEN_BOARD = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/coeq1hmg_woodenboard.gif";
const STORE_SPRITE = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/dv6lmfw5_sprite-max-px-36.gif";
const ORESTORE_PANEL_SCREENSHOT = "https://customer-assets.emergentagent.com/job_79b2228c-ae28-4f93-a50d-47b50e01abcd/artifacts/rrxk2q3j_orestore2.PNG";
const ORESTORE_PANEL_URL = "https://orebosstracker.com/orestore";

const formatMesos = (n) => {
  const v = Math.floor(Number(n) || 0);
  return v.toLocaleString("en-US").replace(/,/g, ".");
};

// Price tier coloring requested by user:
//   < 1.000          → blue
//   1.000 – 9.999.999 → green
//   ≥ 10.000.000      → red
const priceColor = (n) => {
  const v = Number(n) || 0;
  if (v < 1000) return "#3aa3d9";
  if (v < 10000000) return "#33c47f";
  return "#e84150";
};

// BBCode-formatted price cell: tier-colored bold number + " mesos" suffix.
const formatPriceBBC = (n) =>
  `[B][COLOR=${priceColor(n)}]${formatMesos(n)}[/COLOR][/B] [SIZE=2]mesos[/SIZE]`;

const formatUntil = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Returns { active: bool, special: number, until: iso } when a discount is currently live.
const activeSpecial = (item) => {
  const sp = item.special_price;
  const until = item.special_price_until;
  if (!sp || sp <= 0 || !until) return { active: false };
  const d = new Date(until);
  if (Number.isNaN(d.getTime())) return { active: false };
  if (d.getTime() < Date.now()) return { active: false };
  // only show as discount if different from base sell price
  if (item.sell_price && Number(sp) === Number(item.sell_price)) return { active: false };
  return { active: true, special: sp, until };
};

const CATEGORY_ORDER = ["Ores", "Plates", "Crystals"];
const CATEGORY_COLORS = {
  Ores: "#e07a1f",      // maple orange
  Plates: "#3aa3d9",    // sky blue
  Crystals: "#c64bd2",  // magenta
};

/**
 * Fancy MapleRoyals (XenForo) BBCode for the OreStore shop thread.
 * mode: "buy"  => uses item.buy_price (we PAY sellers)
 *       "sell" => uses item.sell_price + optional special_price/special_price_until
 */
const buildBBCode = (categories, mode, maxBonus = 20, bonusGuilds = []) => {
  const isSell = mode === "sell";
  const title = isSell ? "OreStore Sell Prices" : "OreStore Buy Prices";
  const subtitle = isSell
    ? "We are selling the following items at these rates:"
    : "We are buying the following items at these rates:";
  const priceField = isSell ? "sell_price" : "buy_price";

  let out = "";

  // --- Banner: logo + animated store sprites + sign + replenish ---
  out += `[CENTER]\n`;
  out += `[IMG]${ORESTORE_LOGO}[/IMG]\n`;
  out += `[IMG]${WOODEN_BOARD}[/IMG] [IMG]${STORE_SPRITE}[/IMG]\n`;
  out += `[SIZE=4][B][COLOR=#ff9933]Free Market 15 — Channel 15[/COLOR][/B][/SIZE]\n`;
  out += `[SIZE=2][I][COLOR=#a0825a]Stock replenished daily • 22:00 – 01:00 server time[/COLOR][/I][/SIZE]\n`;
  out += `[/CENTER]\n\n`;

  // --- Mission (centered, mixed colors, no yellow walls) ---
  out += `[CENTER]\n`;
  out += `[SIZE=5][B][COLOR=#ff9933]✦ Our Mission ✦[/COLOR][/B][/SIZE]\n\n`;
  out += `[SIZE=3][B][COLOR=#ffd84d]Ore Store's main mission[/COLOR][/B] is to make all minerals, raw and refined available in [B]one place, everyday[/B]. To offer a constant stable service for the community & help you save owls.[/SIZE]\n\n`;
  out += `[SIZE=3]We are always at the same place at [B][COLOR=#33c47f]Free Market 15[/COLOR][/B] on [B][COLOR=#33c47f]Channel 15[/COLOR][/B]. Our stock is replenished everyday, and sometimes [I]few times a day[/I] if there is need for that.[/SIZE]\n\n`;
  out += `[SIZE=3]Stock is replenished mainly at [B][COLOR=#3aa3d9]22:00 – 01:00 server time[/COLOR][/B]. We also buy all kind of [B]ores, raw and refined, jewels and crystals, rocks[/B]. You can find more information about this down below.[/SIZE]\n\n`;
  out += `[SIZE=2][I][COLOR=#a0825a]Prices are reflective of demand and the hard work it is to have store the up 24/7 fully stocked.[/COLOR][/I][/SIZE]\n`;
  out += `[/CENTER]\n\n`;

  out += `[CENTER][SIZE=6][B][COLOR=#ff9933]${title}[/COLOR][/B][/SIZE]\n`;
  out += `[SIZE=3][I]${subtitle}[/I][/SIZE][/CENTER]\n\n`;

  // Buy-mode: link sellers back to the OreStore selling panel
  if (!isSell) {
    out += `[CENTER]\n`;
    out += `[SIZE=4][B][COLOR=#ffd84d]Want to sell your ores to us?[/COLOR][/B][/SIZE]\n`;
    out += `[SIZE=3]Use our online OreStore selling panel — calculate your payout (with the bonus) and submit an offer in seconds. We will contact you in-game or on Discord to arrange the meeting.[/SIZE]\n`;
    out += `[URL='${ORESTORE_PANEL_URL}'][SIZE=5][B][COLOR=#33c47f]>>> ${ORESTORE_PANEL_URL} <<<[/COLOR][/B][/SIZE][/URL]\n`;
    out += `[URL='${ORESTORE_PANEL_URL}'][IMG]${ORESTORE_PANEL_SCREENSHOT}[/IMG][/URL]\n`;
    out += `[SIZE=2][I][COLOR=#a0825a]↑ Click the screenshot or the link above to open the panel.[/COLOR][/I][/SIZE]\n`;
    out += `[/CENTER]\n\n`;
  }

  // --- Category sections ---
  for (const cat of CATEGORY_ORDER) {
    const items = (categories?.[cat] || []).filter(
      (it) => it.enabled !== false && it[priceField] != null && it[priceField] > 0
    );
    if (items.length === 0) continue;

    const color = CATEGORY_COLORS[cat] || "#ffffff";
    out += `[CENTER]\n`;
    out += `[SIZE=6][B][COLOR=${color}]✦ ${cat} ✦[/COLOR][/B][/SIZE]\n`;
    out += `[TABLE]\n`;
    out += `[TR][TD][B][COLOR=${color}]Image[/COLOR][/B][/TD][TD][B][COLOR=${color}]Item[/COLOR][/B][/TD][TD][B][COLOR=${color}]Price (mesos)[/COLOR][/B][/TD][/TR]\n`;

    for (const it of items) {
      const basePrice = formatMesos(it[priceField]);
      const img = it.image_url || "";
      const sp = isSell ? activeSpecial(it) : { active: false };

      const priceCell = sp.active
        ? `[COLOR=#9a8e7a][S]${formatMesos(it[priceField])}[/S][/COLOR]  ${formatPriceBBC(sp.special)}  [SIZE=2][I][COLOR=#cc3344](until ${formatUntil(sp.until)})[/COLOR][/I][/SIZE]`
        : formatPriceBBC(it[priceField]);

      out += `[TR][TD][CENTER][IMG]${img}[/IMG][/CENTER][/TD][TD][CENTER][B]${it.name}[/B][/CENTER][/TD][TD][CENTER]${priceCell}[/CENTER][/TD][/TR]\n`;
    }

    out += `[/TABLE]\n`;
    out += `[/CENTER]\n\n`;
  }

  // Buy-mode: bonus ores section
  if (!isSell) {
    const allItems = CATEGORY_ORDER.flatMap((c) => categories?.[c] || []);
    const bonusItems = allItems.filter(
      (it) => it.enabled !== false && (it.bonus_per_unit || 0) > 0
    );

    out += `[CENTER]\n`;
    out += `[SIZE=6][B][COLOR=#c64bd2]✦ Bonus Ores ✦[/COLOR][/B][/SIZE]\n`;
    out += `[SIZE=3][I]Sell these to us for an extra payout bonus on top of the base price.[/I][/SIZE]\n`;
    out += `[/CENTER]\n\n`;

    if (bonusItems.length > 0) {
      out += `[CENTER][TABLE]\n`;
      out += `[TR][TD][B][COLOR=#c64bd2]Image[/COLOR][/B][/TD][TD][B][COLOR=#c64bd2]Item[/COLOR][/B][/TD][TD][B][COLOR=#c64bd2]Bonus per unit[/COLOR][/B][/TD][/TR]\n`;
      for (const it of bonusItems) {
        const img = it.image_url || "";
        out += `[TR][TD][CENTER][IMG]${img}[/IMG][/CENTER][/TD][TD][B]${it.name}[/B][/TD][TD][B][COLOR=#c64bd2]+${it.bonus_per_unit}%[/COLOR][/B] per unit[/TD][/TR]\n`;
      }
      out += `[/TABLE][/CENTER]\n\n`;
    }

    // Bonus rules
    out += `[CENTER][SIZE=4][B][COLOR=#ffae00]Bonus rules[/COLOR][/B][/SIZE][/CENTER]\n`;
    out += `[LIST]\n`;
    out += `[*]Each unit of a bonus ore adds its [B]bonus per unit[/B] (% of the base payout) to your offer total.\n`;
    out += `[*]Members of a [B]Bonus Guild[/B] (see list below) receive an additional flat bonus on top of item bonuses.\n`;
    out += `[*]The combined bonus (items + guild) is capped at [B][COLOR=#ffae00]${maxBonus}%[/COLOR][/B] of the base payout — you cannot exceed this cap, no matter how many bonus ores or guilds are stacked.\n`;
    out += `[*]Bonus is calculated automatically by the online selling panel — you do not need to compute it yourself.\n`;
    out += `[*]Mixing different bonus ores is allowed; their bonuses stack up to the cap.\n`;
    out += `[/LIST]\n\n`;

    // Bonus Guilds section
    const guilds = (bonusGuilds || []).slice().sort((a, b) => (b.bonus_pct || 0) - (a.bonus_pct || 0));
    if (guilds.length > 0) {
      out += `[CENTER]\n`;
      out += `[SIZE=6][B][COLOR=#3aa3d9]✦ Bonus Guilds ✦[/COLOR][/B][/SIZE]\n`;
      out += `[SIZE=3][I]Active members of these guilds get a flat extra bonus on every offer.[/I][/SIZE]\n`;
      out += `[TABLE]\n`;
      out += `[TR][TD][B][COLOR=#3aa3d9]Guild[/COLOR][/B][/TD][TD][B][COLOR=#3aa3d9]Bonus[/COLOR][/B][/TD][/TR]\n`;
      for (const g of guilds) {
        out += `[TR][TD][B]${g.name}[/B][/TD][TD][B][COLOR=#3aa3d9]+${g.bonus_pct}%[/COLOR][/B][/TD][/TR]\n`;
      }
      out += `[/TABLE]\n`;
      out += `[/CENTER]\n\n`;

      out += `[CENTER]\n`;
      out += `[SIZE=4][B][COLOR=#ff6b6b]⚠ Guild Bonus — Membership Disclaimer[/COLOR][/B][/SIZE]\n`;
      out += `[SIZE=3]You must be an [B]active in-game member[/B] of one of the guilds listed above to claim the guild bonus. Membership is verified in-game at the time of meeting; the bonus may be denied if your character is not part of the declared guild. Guild membership selection is honor-based when submitting the offer through the online panel.[/SIZE]\n`;
      out += `[/CENTER]\n\n`;
    }
  }

  // --- Footer ---
  out += `[CENTER]\n`;
  out += `[IMG]${WOODEN_BOARD}[/IMG]\n`;
  out += `[SIZE=3][I][COLOR=#a0825a]Prices update regularly. Find us at Free Market 15 — Channel 15, or contact us on Discord.[/COLOR][/I][/SIZE]\n`;
  if (!isSell) {
    out += `[SIZE=3][B][COLOR=#ffae00][URL='${ORESTORE_PANEL_URL}']Submit your offer here →[/URL][/COLOR][/B][/SIZE]\n`;
  }
  out += `[/CENTER]`;

  return out;
};

const AdminOreStoreBBCode = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ categories: {} });
  const [guilds, setGuilds] = useState([]);
  const [mode, setMode] = useState("buy");
  const [copied, setCopied] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const [itemsRes, guildsRes] = await Promise.all([
        axios.get(`${API_URL}/api/orestore/items`),
        axios.get(`${API_URL}/api/orestore/guilds`),
      ]);
      setData(itemsRes.data || { categories: {} });
      setGuilds(guildsRes.data?.guilds || []);
    } catch (e) {
      toast.error("Failed to load OreStore items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const bbcode = useMemo(
    () => buildBBCode(data.categories, mode, data.max_bonus_capacity, guilds),
    [data, mode, guilds]
  );

  const counts = useMemo(() => {
    const priceField = mode === "buy" ? "buy_price" : "sell_price";
    let total = 0;
    let specials = 0;
    for (const cat of CATEGORY_ORDER) {
      for (const it of data.categories?.[cat] || []) {
        if (it.enabled === false) continue;
        if (!it[priceField] || it[priceField] <= 0) continue;
        total += 1;
        if (mode === "sell" && activeSpecial(it).active) specials += 1;
      }
    }
    return { total, specials };
  }, [data, mode]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bbcode);
      setCopied(true);
      toast.success("BBCode copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy. Select the text and copy manually.");
    }
  };

  return (
    <Card className="bg-card/50" data-testid="admin-orestore-bbcode-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <img src={ORESTORE_LOGO} alt="OreStore" className="h-7 w-auto" />
              <span>OreStore Forum BBCode Generator</span>
            </CardTitle>
            <CardDescription>
              Generate ready-to-paste BBCode for the MapleRoyals forum shop thread.
              Switch between Buy and Sell tabs, then click Copy. Active promo discounts
              (special_price + special_price_until) are auto-rendered with crossed-out base price.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchItems}
            disabled={loading}
            data-testid="bbcode-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh prices
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <Tabs value={mode} onValueChange={setMode} data-testid="bbcode-mode-tabs">
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="buy" data-testid="bbcode-tab-buy">
                Buy prices
              </TabsTrigger>
              <TabsTrigger value="sell" data-testid="bbcode-tab-sell">
                Sell prices
              </TabsTrigger>
            </TabsList>

            {["buy", "sell"].map((m) => (
              <TabsContent key={m} value={m} className="mt-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Code className="w-3.5 h-3.5" />
                    <span>
                      {counts.total} item{counts.total === 1 ? "" : "s"} included
                      {m === "sell" && counts.specials > 0 && (
                        <>
                          {" • "}
                          <span className="text-rose-400 font-bold">{counts.specials} promo</span>
                        </>
                      )}
                      {" • "}XenForo / MapleRoyals BBCode format
                    </span>
                  </div>
                  <Button
                    onClick={handleCopy}
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-500 text-white"
                    data-testid={`bbcode-copy-btn-${m}`}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    {copied ? "Copied!" : "Copy BBCode"}
                  </Button>
                </div>
                <Textarea
                  readOnly
                  value={bbcode}
                  rows={18}
                  className="font-mono text-xs bg-black/40 border-amber-700/40"
                  onFocus={(e) => e.target.select()}
                  data-testid={`bbcode-textarea-${m}`}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminOreStoreBBCode;
